import React from 'react';
import type { ValueType } from 'react-select/lib/types';

import * as styles from './styles';
import { v4 as uuidv4 } from 'uuid';

import type { UUID, Vehicle } from '@cobliteam/common-types';

import { isWebDataError, isWebDataFetching, isWebDataSuccess, type Recurrence, type WebData } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import {
  BodyText,
  ButtonGroup,
  DescriptionListMedium,
  DescriptionTitle,
  DialogWithHeader,
  type ISelectOptionType,
  Loading,
  ModalType,
  NoConnectionIcon,
  Placeholder,
  PlaceholderVehicleIcon,
  Select,
} from '@cobliteam/base-components';

import { LIMIT_NUMBER_RECURRENCES } from '../constants';
import { RecurrenceInputs, type RecurrenceItems } from '../RecurrenceInputs/RecurrenceInputs';

export interface VehicleRecurrences {
  vehicle: Vehicle;
  recurrences: Recurrence[];
}

export interface EditRemindersModalProps {
  vehiclesRecurrencesWebData: WebData<VehicleRecurrences[]>;
  onClose: () => void;
  onConfirm: (vehicleId: UUID, recurrences: Recurrence[]) => void;
}

export const EditRemindersModal: React.FC<EditRemindersModalProps> = ({ vehiclesRecurrencesWebData, onClose, onConfirm }) => {
  const [wantedVehicleId, setWantedVehicleId] = React.useState<UUID | undefined>(undefined);
  const [recurrenceItems, setRecurrenceItems] = React.useState<RecurrenceItems>({});
  const [canBeSubmited, setCanBeSubmited] = React.useState(false);
  const [hadAnyChange, setHadAnyChange] = React.useState(false);

  const handleConfirm = () => {
    Analytics.track('Maintenance Reminders Edit Confirmed');

    const recurrences = Object.values(recurrenceItems).map((recurrence) => ({
      recurrenceInKm: recurrence.valueInKm || 0,
      recurrenceName: recurrence.name,
    }));

    if (wantedVehicleId) {
      onConfirm(wantedVehicleId, recurrences);
    }
  };

  const handleVehicleFilterChange = (vehiclesRecurrences: VehicleRecurrences[]) => (selected: ValueType<ISelectOptionType>) => {
    const { value } = selected as ISelectOptionType;

    const vehicleRecurrences = vehiclesRecurrences.find((vr) => vr.vehicle.id === value);
    const recurrencesArray = vehicleRecurrences ? vehicleRecurrences.recurrences : [];

    const newRecurrenceItems = recurrencesArray.reduce((itemsAcc, recurrence) => {
      const itemId = uuidv4();
      return {
        ...itemsAcc,
        [itemId]: {
          id: itemId,
          valueInKm: recurrence.recurrenceInKm,
          name: recurrence.recurrenceName,
        },
      };
    }, {});

    setWantedVehicleId(value);
    setRecurrenceItems(newRecurrenceItems);
    setCanBeSubmited(false);
    setHadAnyChange(false);
  };

  const handleRecurrenceItemsChange = (newRecurrenceItems: RecurrenceItems) => {
    const hasError = Object.values(newRecurrenceItems).some((item) => !item.valueInKm || item.error);

    setCanBeSubmited(!hasError);
    setHadAnyChange(true);
    setRecurrenceItems(newRecurrenceItems);
  };

  const renderVehicleSelect = (vehiclesRecurrences: VehicleRecurrences[]) => {
    const options = vehiclesRecurrences
      .map(({ vehicle }) => ({
        label: vehicle.label,
        value: vehicle.id,
      }))
      .sort((a, b) => (b.label < a.label ? 1 : -1));

    return (
      <>
        <DescriptionListMedium>Veículo</DescriptionListMedium>
        <Select
          placeholder='Selecionar veículo'
          className={styles.select}
          value={options.find((v) => v.value === wantedVehicleId)}
          onChange={handleVehicleFilterChange(vehiclesRecurrences)}
          maxMenuHeight={130}
          options={options}
        />
      </>
    );
  };

  return (
    <DialogWithHeader
      open
      shouldConfirm={hadAnyChange}
      maxWidth='md'
      title='Editar lembretes'
      actions={<ButtonGroup onCancel={onClose} onConfirm={handleConfirm} buttonText='Salvar' confirmationButtonProps={{ disabled: !canBeSubmited }} type={ModalType.SUBMIT} />}
      onCloseButtonClick={onClose}
      scroll='body'
    >
      <div className={styles.wrapper}>
        <BodyText>
          {`Edite a recorrência e o que precisa ser feito nos lembretes de um veículo. Adicione até
            ${LIMIT_NUMBER_RECURRENCES} lembretes.`}
        </BodyText>
        {isWebDataSuccess(vehiclesRecurrencesWebData) && vehiclesRecurrencesWebData.data.length > 0 && renderVehicleSelect(vehiclesRecurrencesWebData.data)}
        {isWebDataSuccess(vehiclesRecurrencesWebData) && vehiclesRecurrencesWebData.data.length === 0 && (
          <Placeholder className={styles.placeholderWithoutTextButton} text='Não há veículos com lembretes de manutenção configurados.' Icon={PlaceholderVehicleIcon} />
        )}
        {isWebDataError(vehiclesRecurrencesWebData) && (
          <Placeholder className={styles.placeholderWithoutTextButton} text='Ops! Ocorreu um erro no sistema.' Icon={NoConnectionIcon} />
        )}
        {isWebDataFetching(vehiclesRecurrencesWebData) && <Loading className={styles.loading} />}

        {wantedVehicleId && (
          <div className={styles.input}>
            <DescriptionTitle>Lembretes</DescriptionTitle>
            <RecurrenceInputs items={recurrenceItems} onItemsChange={handleRecurrenceItemsChange} />
          </div>
        )}
      </div>
    </DialogWithHeader>
  );
};
